/*
 * Top level module for a single Topic.  Nothing going on really except setting the layout and loading submodules.
 */

define([
  "marionette",
  "backbone",
  "jquery",
  "underscore",
  "configuration/properties",
  "messaging/messenger",
  "modules/shared/sortableList/sortableListModule",
  "modules/topicsSplit/topic/topicView",
  "modules/topicsSplit/topic/topicContext/topicContextModule",
  "modules/topicsSplit/topic/topicStatus/topicStatusModule",
  "modules/topicsSplit/topic/topicTitle/topicTitleModule",
  "modules/topicsSplit/topic/topicScheduledStatus/topicScheduledStatusModule",
  "modules/topicsSplit/topic/topicExamples/topicExamplesModule",
  "modules/topicsSplit/topic/topicConversationPreview/topicConversationPreviewModule",
  "modules/topicsSplit/topic/topicConversation/topicConversationModule",
  "modules/topicsSplit/topic/topicDetails/topicDetailsModule",
  "modules/topicsSplit/topic/topicLabel/topicLabelModule",
  "modules/topicsSplit/topic/topicPerformance/topicPerformanceModule",
  "modules/topicsSplit/topic/topicFolder/topicFolderModule",
  "modules/topicsSplit/topic/topicSurvey/topicSurveyModule",
  "modules/topicsSplit/topic/topicModel",
  "modules/topicsSplit/topic/topicTasks/topicTasksModule",
  "modules/topicsSplit/topic/topicUsage/topicUsageModule",
  "modules/header/kbMenu/kbMenuModel",
], function (
  Marionette,
  Backbone,
  $,
  _,
  Properties,
  Messenger,
  SortableListModule,
  TopicView,
  TopicContextModule,
  TopicStatusModule,
  TopicTitleModule,
  TopicScheduledStatusModule,
  TopicExamplesModule,
  TopicConversationPreviewModule,
  TopicConversationModule,
  TopicDetailsModule,
  TopicLabelModule,
  TopicPerformanceModule,
  TopicFolderModule,
  TopicSurveyModule,
  TopicModel,
  TopicTasksModule,
  TopicUsageModule,
  KbMenuModel,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "topic";
      this.region = options.region;
      this.topic = null;
      this.rootTopicGroup = options.rootTopicGroup;
      this._setUpVents();
      this._setUpReqres();
    },
    save: function (options) {
      console.log("saving topic");
      const savingOptions = _.extend({ validate: true }, options);
      // Must manually validate, so that the examples are properly validated even if the Topic
      // is not dirty.
      // Using _validate because it automatically takes care of things like filling in the
      // attributes to validate and triggering "invalid" if there are any issues.
      if (!this.topic._validate(null, savingOptions)) {
        return false;
      }
      if (this.topic.isNew()) {
        this.topic.once("sync", this._syncBackboneHistory, this);
      }
      return this.topic.saveModel(savingOptions);
    },
    show: function (topic) {
      topic.onSynced(function () {
        if (topic.isNew()) {
          document.title = $.t("topic.newTopic") + " - " + Properties.applicationName;
        } else {
          document.title = topic.get("intentName") + " - " + Properties.applicationName;
        }
      });
      //if the topic cannot be loaded, then show an error message and send them to the topics page
      this.listenToOnce(
        topic,
        "error",
        function (_model, resp) {
          Messenger.showErrorFromResponse(resp);
          //if there is an error loading the topic, then redirect to the topics page
          Messenger.vent.trigger("show:topics");
        },
        this,
      );
      this.performanceLoaded = false;
      this.surveyLoaded = false;
      this.topic = topic;
      this.kb = Messenger.reqres.request("active:kb:model");
      const kbId = this.kb.get("kbId");
      this.topic.allowCreate = Messenger.reqres.request("can:create:topic", kbId);
      this.topic.allowDelete = Messenger.reqres.request("can:delete:topic", kbId);
      this.topic.allowArchive = Messenger.reqres.request("can:archive:topic");
      this.topic.languages = Messenger.reqres.request("kb:languages");
      this.topic.selectedLocale = Messenger.reqres.request("default:response:language");
      this.topic.readOnly = Messenger.reqres.request("topic:read:only");
      this.layout = new TopicView.topicLayout({ model: this.topic });
      this.context = this.addSubModule(new TopicContextModule({ region: this.layout.context }));
      this.status = this.addSubModule(new TopicStatusModule({ region: this.layout.status }));
      this.title = this.addSubModule(new TopicTitleModule({ region: this.layout.title }));
      this.usage = this.addSubModule(new TopicUsageModule({ region: this.layout.usage }));
      if (!this.topic.readOnly) {
        this.scheduledStatus = this.addSubModule(
          new TopicScheduledStatusModule({ region: this.layout.scheduledStatus }),
        );
      }
      if (Messenger.reqres.request("can:view:topicExamples")) {
        this.examples = this.addSubModule(
          new TopicExamplesModule({
            region: this.layout.examples,
            topic: this.topic,
          }),
        );
      }
      this.conversationPreview = this.addSubModule(
        new TopicConversationPreviewModule({ region: this.layout.conversation }),
      );
      this.conversation = this.addSubModule(
        new TopicConversationModule({ region: this.layout.advancedConversation }),
      );
      this.details = this.addSubModule(new TopicDetailsModule({ region: this.layout.details }));
      this.label = this.addSubModule(new TopicLabelModule({ region: this.layout.label }));
      this.performance = this.addSubModule(
        new TopicPerformanceModule({ region: this.layout.performance }),
      );
      this.survey = this.addSubModule(new TopicSurveyModule({ region: this.layout.survey }));
      this.folder = this.addSubModule(new TopicFolderModule({ region: this.layout.folder }));
      this.tasks = this.addSubModule(
        new TopicTasksModule({
          region: this.layout.tasks,
          topic: this.topic,
        }),
      );
      //if validation fails, call the appropriate functions to display the error
      this.topic.on(
        "invalid",
        function (_model, error) {
          if ("intentName" in error) {
            this.title.nameError(error["intentName"]);
          }
          if ("intentExamples" in error && this.examples) {
            this.examples.emptyError(error["intentExamples"]);
          }
        },
        this,
      );
      //handle deleting a topic
      this.layout.on(
        "delete:topic",
        function () {
          const topicToDelete = this.topic;
          //delete after user confirmation
          Messenger.yesNoDialog(
            "topic.dialog.delete",
            {
              yes: function () {
                Messenger.vent.trigger("track:interaction", {
                  action: "Delete Topic",
                  id: topicToDelete.id,
                  idType: "Topic",
                  object: "Topic",
                  source: "Topic Details",
                });
                Messenger.vent.trigger("cancel:textbox"); //cancel any open textboxes
                topicToDelete.deleteModel({
                  success: () => Messenger.vent.trigger("show:topics"),
                });
              },
            },
            this,
          );
        },
        this,
      );
      this.layout.on(
        "save:topic",
        function () {
          Messenger.save();
        },
        this,
      );

      this.layout.on("archive:topic", this._archiveTopic, this);

      this.layout.on("restore:topic", this._restoreTopic, this);

      //handle adding another topic
      this.layout.on(
        "add:another:topic",
        function () {
          Messenger.vent.trigger("add:topic");
        },
        this,
      );
      this.layout.on(
        "show",
        function () {
          this._showSubViews(topic);
        },
        this,
      );
      this.layout.on("show:authoring", (e) =>
        Messenger.reqres
          .request("topic:saveIfNew")
          .catch(() => this.layout.showTab(e.relatedTarget)),
      );
      this.layout.on("show:conversation", (e) =>
        Messenger.reqres
          .request("topic:saveIfNew")
          .catch(() => this.layout.showTab(e.relatedTarget)),
      );
      //handle showing the performance page
      this.layout.on(
        "show:performance",
        function (e) {
          if (!this.performanceLoaded) {
            this.performanceLoaded = true; //don't try to show it again since it's already loaded
            //make sure topic is saved first
            Messenger.reqres
              .request("topic:saveIfNew")
              .then(() => this.performance.show(this.topic))
              .catch(() => this.layout.showTab(e.relatedTarget));
          } else {
            Messenger.reqres
              .request("topic:saveIfNew")
              .catch(() => this.layout.showTab(e.relatedTarget));
          }
        },
        this,
      );
      this.layout.on(
        "show:survey",
        function (e) {
          if (!this.surveyLoaded) {
            this.surveyLoaded = true; //don't try to show it again since it's already loaded
            //make sure topic is saved first
            Messenger.reqres
              .request("topic:saveIfNew")
              .then(() => this.survey.show(this.topic))
              .catch(() => this.layout.showTab(e.relatedTarget));
          } else {
            Messenger.reqres
              .request("topic:saveIfNew")
              .catch(() => this.layout.showTab(e.relatedTarget));
          }
        },
        this,
      );
      this.layout.on("show:tasks", (e) =>
        Messenger.reqres
          .request("topic:saveIfNew")
          .catch(() => this.layout.showTab(e.relatedTarget)),
      );
      this.layout.on("topic:history", this._showTopicHistory, this);
      const downloadCookie = Messenger.reqres.request("get:download:cookie");
      this.showRegion(this.layout, { loading: true, entity: downloadCookie });
    },
    _showSubViews: function (topic) {
      //show performance tab if the topic isn't new
      if (!topic.isNew()) {
        this.layout.showPerformanceTab();
        this.layout.showSurveyTab();
      }
      this.context.show(topic);
      this.status.show(topic);
      if (!topic.readOnly) {
        this.scheduledStatus.show(topic);
      }
      this.kb.onSynced(function () {
        if (this.kb.get("taskManagement")) {
          this.layout.showTasks();
          this.tasks.show(topic.tasks());
        }
      }, this);
      if (this.examples) {
        this.examples.show(topic.examples());
      }
      //pass in the live conversation root node in case we eventually load the live conversation
      this.conversationPreview.show(topic.conversation(), topic.liveConversation(false));
      this.conversation.show(topic.conversation(), topic.liveConversation(false));
      this.details.show(topic);
      this.label.show(topic.topicLabels(), topic.touchPointLabels(), topic.get("intentId"));
      this.title.show(topic);
      this.usage.show(topic);
      this.folder.show(topic, this.rootTopicGroup);
      Messenger.vent.trigger("topic:fully:loaded");
    },
    _archiveTopic: function () {
      const topicToArchive = this.topic;
      Messenger.yesNoDialog(
        "topic.dialog.archive",
        {
          yes: () => {
            Messenger.vent.trigger("track:interaction", {
              action: "Archive Topic",
              id: topicToArchive.id,
              idType: "Topic",
              object: "Topic",
              source: "Topic Details",
            });
            Messenger.vent.trigger("cancel:textbox"); //cancel any open textboxes
            // save any pending topic changes
            Messenger.save((success) => {
              if (success) {
                // archive topic
                const archiveTopicModel = new TopicModel.archiveTopic({
                  intentId: topicToArchive.get("intentId"),
                });
                archiveTopicModel
                  .saveModelPromise()
                  .then(() => {
                    topicToArchive.set("archived", true);
                    Messenger.vent.trigger("show:topics");
                  })
                  .catch((error) => {
                    Messenger.okDialog(error);
                  })
                  .finally(() => {
                    Messenger.vent.trigger("close:saving:display");
                  });
              } else {
                Messenger.okDialog("Could not save topic");
              }
            });
          },
        },
        this,
      );
    },
    _restoreTopic: function () {
      const topicToRestore = this.topic;
      Messenger.yesNoDialog(
        "topic.dialog.restore",
        {
          yes: () => {
            Messenger.vent.trigger("show:saving:display");
            Messenger.vent.trigger("track:interaction", {
              action: "Restore Topic",
              id: topicToRestore.id,
              idType: "Topic",
              object: "Topic",
              source: "Topic Details",
            });
            // Any textbox should not be open because the topic is archived and editing should
            // not be allowed. So, there are no topic changes to save at this point. And we
            // cannot use Messenger.save() because the endpoint to update topic will not allow
            // updating archived topic.
            const restoreTopicModel = new TopicModel.restoreTopic({
              intentId: topicToRestore.get("intentId"),
            });
            //trigger navigating to the topics page
            restoreTopicModel
              .saveModelPromise()
              .then(() => {
                topicToRestore.set("archived", false);
                Messenger.vent.trigger("show:topics");
              })
              .catch((error) => {
                Messenger.okDialog(error);
              })
              .finally(() => {
                Messenger.vent.trigger("close:saving:display");
              });
          },
        },
        this,
      );
    },
    _setUpReqres: function () {
      Messenger.reqres.setHandler("current:topic", () => this.topic, this);
      Messenger.reqres.setHandler("current:topic:id", () => this.topic.id, this);
      Messenger.reqres.setHandler("current:topic:status", () => this.topic.getStatus(), this);
      Messenger.reqres.setHandler(
        "topic:saveIfNew",
        () =>
          new Promise((resolve, reject) => {
            // trigger saving any textbox model so that attributes are validated before saving the
            // topic and if there is any error, do not save the topic
            const saveSuccess = Messenger.reqres.request("save:textbox");
            if (!saveSuccess) {
              reject("", this.topic);
              return;
            }
            if (!this.topic.isNew()) {
              resolve(this.topic);
            } else {
              this.topic.saveModel({
                topLevelOnly: true,
                error: (_model, resp) => {
                  const error = Messenger.getErrorFromResponse(resp);
                  Messenger.setErrorMsgFromResponse(resp);
                  Messenger.saveAttempted(false);
                  reject(error, this.topic);
                },
                success: () => {
                  Messenger.saveAttempted(true);
                  this._syncBackboneHistory();
                  resolve(this.topic);
                },
              });
            }
          }),
        this,
      );
      Messenger.reqres.setHandler(
        "topic:statuses",
        (topic) => {
          topic = topic || this.topic;
          //decide the possible transitions from this status and rerender the view
          const conversation = topic.conversation();
          const kbModel = Messenger.reqres.request("active:kb:model");
          const statuses = Messenger.reqres.request("kb:statuses");
          return Promise.all([
            statuses.onSyncedPromise(),
            conversation.onSyncedPromise(),
            kbModel.onSyncedPromise(),
          ]).then(() => {
            let allowDisable = false;
            let allowEnable = false;
            let allowPublish = false;
            const responseVersioning = kbModel.get("responseVersioning");
            const topicStatusId = topic.getStatus();
            //iterate through status transitions and add transitions from the current status
            const transitions = new Backbone.Collection(null, {
              comparator: function (transition) {
                //perform a primary sort by target status and a secondary sort by the status transition
                //This allows us to order the transitions correctly in the dropdown (Publish All, Publish...,
                // Disable)
                return transition.get("targetStatusId") + "-" + transition.get("transitionName");
              },
            });
            if (responseVersioning) {
              if (topicStatusId == KbMenuModel.statuses.VERSIONED_CREATED) {
                allowPublish = true;
                allowDisable = true;
              } else {
                conversation.walkAllResponseTemplates((responseTemplate) => {
                  const statusId = responseTemplate.get("statusId");
                  switch (statusId) {
                    case KbMenuModel.statuses.VERSIONED_DISABLED:
                      allowPublish = true;
                      break;
                    case KbMenuModel.statuses.VERSIONED_CREATED:
                    case KbMenuModel.statuses.VERSIONED_PENDING_RESPONSE:
                      allowPublish = true;
                      allowDisable = true;
                      break;
                    case KbMenuModel.statuses.VERSIONED_PUBLISHED:
                      allowDisable = true;
                      break;
                  }
                });
              }
              if (allowPublish) {
                transitions.add({
                  sourceStatusId: topicStatusId,
                  targetStatusId: KbMenuModel.statuses.VERSIONED_PUBLISHED,
                  transitionName: "topic.statuses.publishAll",
                });
                transitions.add({
                  sourceStatusId: topicStatusId,
                  targetStatusId: KbMenuModel.statuses.VERSIONED_PUBLISHED,
                  transitionName: "topic.statuses.publishSelected",
                });
              }
              if (allowDisable) {
                transitions.add({
                  sourceStatusId: topicStatusId,
                  targetStatusId: KbMenuModel.statuses.VERSIONED_DISABLED,
                  transitionName: "topic.statuses.disableAll",
                });
                transitions.add({
                  sourceStatusId: topicStatusId,
                  targetStatusId: KbMenuModel.statuses.VERSIONED_DISABLED,
                  transitionName: "topic.statuses.disableSelected",
                });
              }
            } else {
              if (topicStatusId == KbMenuModel.statuses.NON_VERSIONED_CREATED) {
                allowEnable = true;
                allowDisable = true;
              } else {
                conversation.walkAllResponseTemplates((responseTemplate) => {
                  const statusId = responseTemplate.get("statusId");
                  switch (statusId) {
                    case KbMenuModel.statuses.NON_VERSIONED_CREATED:
                      allowEnable = true;
                      allowDisable = true;
                      break;
                    case KbMenuModel.statuses.NON_VERSIONED_DISABLED:
                      allowEnable = true;
                      break;
                    case KbMenuModel.statuses.NON_VERSIONED_ENABLED:
                      allowDisable = true;
                      break;
                  }
                });
              }
              if (allowEnable) {
                transitions.add({
                  sourceStatusId: topicStatusId,
                  targetStatusId: KbMenuModel.statuses.NON_VERSIONED_ENABLED,
                  transitionName: "topic.statuses.enableAll",
                });
                transitions.add({
                  sourceStatusId: topicStatusId,
                  targetStatusId: KbMenuModel.statuses.NON_VERSIONED_ENABLED,
                  transitionName: "topic.statuses.enableSelected",
                });
              }
              if (allowDisable) {
                transitions.add({
                  sourceStatusId: topicStatusId,
                  targetStatusId: KbMenuModel.statuses.NON_VERSIONED_DISABLED,
                  transitionName: "topic.statuses.disableAll",
                });
                transitions.add({
                  sourceStatusId: topicStatusId,
                  targetStatusId: KbMenuModel.statuses.NON_VERSIONED_DISABLED,
                  transitionName: "topic.statuses.disableSelected",
                });
              }
            }
            return transitions;
          });
        },
        this,
      );
    },
    _setUpVents: function () {
      //If the topic is a branching conversation, show the conversation tab for advanced editing
      Messenger.vent.on(
        "show:conversation:tab",
        function () {
          this.layout.showConversationTab();
        },
        this,
      );
      Messenger.vent.on(
        "clicked:diagram",
        function () {
          //make sure we can close any open textboxes before switching tabs
          if (Messenger.reqres.request("save:textbox")) {
            //when the user clicks on the conversation diagram, make sure they
            //get sent to the conversation tab for editing
            this.layout.showConversationPane();
          }
        },
        this,
      );
      //show history of the topic (examples)
      Messenger.vent.on("topic:history", this._showTopicHistory, this);
      //Monitor the topic status to see if we need to show or hide the live conversation
      Messenger.vent.on(
        "set:topic:status",
        function (statusId) {
          //get old showConversation parameter
          const oldShowConversation = this.topic.liveConversation(false).showConversation;
          //status is 'pending response' or 'disabled response'
          if (
            statusId === KbMenuModel.statuses.VERSIONED_PENDING_RESPONSE ||
            statusId === KbMenuModel.statuses.VERSIONED_DISABLED
          ) {
            //fetch the conversation
            this.topic.liveConversation(true).showConversation = true;
            console.log("get live conversation");
          } else {
            this.topic.liveConversation(false).showConversation = false;
          }
          if (oldShowConversation !== this.topic.liveConversation(false).showConversation) {
            //trigger change event to notify listeners of the parameter change
            this.topic.liveConversation(false).trigger("showConversation");
          }
        },
        this,
      );

      //Apply a mart transcript to this topic.
      // A new example should be created with the utterance set as the example.
      // If new, this means setting the title and label, as well.
      Messenger.vent.on(
        "apply:transcript:to:topic",
        function (transcript, localeId) {
          if (this.topic.isNew()) {
            const trimmedUtterance = transcript.get("userUtterance").trimStrict();
            //close the textbox that is open when we create a new topic
            Messenger.vent.trigger("cancel:textbox");
            //set the topic name using the trimmed utterance
            this.topic.set("intentName", trimmedUtterance);
            //re-render the topic title
            this.title.view.render();
            //fire an "enter:intentName" event to mimic the user saving the topic title in a textbox...
            //this will populate the topic label field
            this.topic.trigger("enter:intentName:locale", localeId);
          }
          if (this.examples) {
            //add the utterance as an example, delete clustering jobs and open the example editor
            this.examples.addTranscriptExample(transcript, localeId);
          } else {
            // At this point, the topic is not yet saved, but go ahead and delete clustering jobs anyway for simplicity.
            Messenger.vent.trigger("delete:clustering:jobs");
          }
        },
        this,
      );

      Messenger.vent.on(
        "conversation:delete:node",
        function (deletedNode) {
          console.log("deleting node " + deletedNode.get("description"));

          this.topic.conversationModel.dirty = true;
          //Since the conversation model is dirty, it will automatically rebuild the model
          //before saving, thus capturing the change in the save.

          //We don't need to set the topic itself as dirty.  If it is dirty for another reason, that will flag
          //it, and it will be saved.  Otherwise, we'll just save the conversation, which is marginally quicker.
          Messenger.save();
        },
        this,
      );

      //Allows updating the number of tasks on the Tasks tab.  This is split into increment/decrement as well, since
      //we won't always want to update it with the exact number of tasks.  Ignored and Approved/Completed tasks
      //shouldn't show up in the count on the tab (nor Not Started copies of Resubmitted ones), but are still
      //present client-side until the topic is saved.
      Messenger.vent.on(
        "update:task:count",
        function (count) {
          this.layout.updateTaskCount(count);
        },
        this,
      );

      Messenger.vent.on(
        "change:example:locale",
        function (localeId) {
          this.topic.selectedLocale = localeId;
          if (this.examples) {
            this.examples.view.render();
            this.examples.view.setSelectedLocale(localeId);
          }
        },
        this,
      );
    },
    _showTopicHistory: async function () {
      if (this.topic) {
        this.recordsPerPage = SortableListModule.DEFAULT_RECORDS_PER_PAGE;
        const historyCollection = new TopicModel.pagedHistoryCollection({
          intentId: this.topic.get("intentId"),
        });
        await historyCollection.pagination(1, this.recordsPerPage);
        const headers = Messenger.reqres.request("topic:history:headers", "lazyLoading");

        Messenger.vent.trigger(
          "show:modal",
          SortableListModule,
          {
            headers: headers,
            recordsPerPage: this.recordsPerPage,
            namespace: "topicHistory",
            lazyLoading: true,
          },
          historyCollection,
          {
            title: "topic.history",
            closeButton: true,
            closeOnBackgroundClick: true,
            modalHeight: "800px",
            modalWidth: "1550px",
            maximizeButton: true,
            trackInteraction: {
              action: "Topic History",
              id: this.topic.id,
              idType: "Topic",
              object: "Topic",
            },
          },
        );
      }
    },
    _syncBackboneHistory: function () {
      // Make a history record for this topic, so we can navigate back to it
      const kbId = Messenger.reqres.request("active:kb");
      Backbone.history.navigate(kbId + "/topic/" + this.topic.get("intentId"));
    },
  });
});
